import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, withQuery, money, date, DOC_TYPE_LABEL, STATUS_LABEL, STATUS_CLASS } from '../api.js';
import { useAuth } from '../App.jsx';

/*
 * Бързи бутони (28.10.2026) — най-ползваните екрани, подредени по броя
 * заявки в дневника на сървъра: продажба, „Чакат продажба“, контрагенти,
 * наличности, каса, длъжници, плащания, доставка, печалба.
 */
const BARZI = [
  { to: '/documents/new/sale', ikona: '🧾', ime: 'Продажба', pishe: true },
  { to: '/chakat-prodazhba', ikona: '⏳', ime: 'Чакат продажба' },
  { to: '/stock', ikona: '📦', ime: 'Наличности' },
  { to: '/cash', ikona: '💶', ime: 'Каса' },
  { to: '/partners', ikona: '👥', ime: 'Контрагенти' },
  { to: '/debtors', ikona: '📋', ime: 'Длъжници' },
  { to: '/payments', ikona: '💳', ime: 'Плащания' },
  { to: '/documents/new/purchase', ikona: '🚚', ime: 'Доставка', pishe: true },
  { to: '/profit', ikona: '📈', ime: 'Печалба' },
];

export default function Dashboard() {
  const { user } = useAuth();
  const canWrite = user?.role !== 'auditor';
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get('/api/reports/dashboard'),
      api.get(withQuery('/api/documents', { type: 'sale', limit: 12 })),
    ])
      .then(([s, d]) => { setStats(s); setRecent(d.items); })
      .catch((err) => setError(err.message));
  }, []);

  if (error) return <div className="error">{error}</div>;
  if (!stats) return <div className="empty">Зареждане…</div>;

  return (
    <>
      <div className="page-head"><h1>Начало</h1></div>

      <div className="tiles">
        <Tile label="Продажби днес" value={money(stats.today.total)} unit="без ДДС"
          sub={`с ДДС ${money(stats.today.gross)} · ${stats.today.count} документа`}
          cmp={<Sravnenie sega={stats.today} kakvo="den" />} />
        <Tile label="Продажби този месец" value={money(stats.month.total)} unit="без ДДС"
          sub={`с ДДС ${money(stats.month.gross)} · ${stats.month.count} документа`}
          cmp={<Sravnenie sega={stats.month} kakvo="mesec" />} />
        <Tile label="Стойност на склада" value={money(stats.stockValue)} unit="без ДДС"
          sub="по доставната цена на всяка партида" />
        <Tile label="Вземания от клиенти" value={money(stats.receivables)} unit="с ДДС"
          sub="неплатени документи" />
        <Tile label="Задължения към доставчици" value={money(stats.payables)} unit="с ДДС"
          sub="неплатени доставки" />
        <Tile
          label="За дозареждане"
          value={String(stats.lowStockCount)}
          sub="продавани скоро, но свършили"
        />
      </div>

      <div className="barzi-butoni">
        {BARZI.filter((b) => canWrite || !b.pishe).map((b) => (
          <Link key={b.to} to={b.to} className="barz-buton">
            <span className="ikona">{b.ikona}</span>
            {b.ime}
          </Link>
        ))}
      </div>

      <div className="card">
        <div className="card-head">
          Последни продажби
          <div style={{ flex: 1 }} />
          <Link to="/documents?type=sale">всички</Link>
        </div>
        <div className="table-wrap">
          <table className="compact">
            <thead>
              <tr>
                <th>Номер</th>
                <th>Дата</th>
                <th>Клиент</th>
                <th>Фактура</th>
                <th className="num">Сума</th>
                <th>Състояние</th>
              </tr>
            </thead>
            <tbody>
              {recent.map((d) => (
                <tr key={d.id}>
                  <td><Link to={`/documents/${d.id}`}>{d.doc_number}</Link></td>
                  <td>{date(d.doc_date)}</td>
                  <td>{d.partner_name ?? <span className="muted">на каса</span>}</td>
                  <td>{d.invoice_no ?? <span className="muted">—</span>}</td>
                  <td className="num">{money(d.total, d.currency)}</td>
                  <td>
                    <span className={`badge ${STATUS_CLASS[d.status]}`}>{STATUS_LABEL[d.status]}</span>
                  </td>
                </tr>
              ))}
              {recent.length === 0 && (
                <tr><td colSpan={6} className="empty">Няма продажби</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

function Tile({ label, value, unit, sub, cmp }) {
  return (
    <div className="tile">
      <div className="label">{label}</div>
      {/* „без ДДС“/„с ДДС“ на реда на сумата (28.10.2026) — плочката е по-ниска. */}
      <div className="value">
        {value}
        {unit && <span className="unit">{unit}</span>}
      </div>
      {sub && <div className="sub">{sub}</div>}
      {cmp}
    </div>
  );
}

/*
 * Стрелка спрямо същия период назад (26.10.2026) — оборот и брой документи.
 * Зелено нагоре, ако е повече; червено надолу, ако е по-малко.
 */
const DNI = ['нд', 'пн', 'вт', 'ср', 'чт', 'пт', 'сб'];
function Strelka({ sega, predi, format }) {
  const a = Number(sega ?? 0);
  const b = Number(predi ?? 0);
  if (a === b) return <span className="muted">= {format(b)}</span>;
  const gore = a > b;
  const pct = b ? Math.round(((a - b) / Math.abs(b)) * 100) : null;
  return (
    <span style={{ color: gore ? 'var(--ok, #15803d)' : 'var(--danger, #b91c1c)', fontWeight: 600, whiteSpace: 'nowrap' }}
      title={`преди: ${format(b)}`}>
      {gore ? '▲' : '▼'} {pct != null ? `${pct > 0 ? '+' : ''}${pct}%` : ''}
      <span className="muted" style={{ fontWeight: 400 }}> ({format(b)})</span>
    </span>
  );
}
function Sravnenie({ sega, kakvo }) {
  const p = sega.prev;
  if (!p) return null;
  const [g, m, d] = String(p.den).split('-');
  const den = new Date(Number(g), Number(m) - 1, Number(d));
  const koga = kakvo === 'den'
    ? `${DNI[den.getDay()]} ${d}.${m} до ${p.chas}`
    : `1–${Number(d)}.${m} до ${p.chas}`;
  return (
    <div className="sub" style={{ marginTop: 4, display: 'flex', flexWrap: 'wrap', gap: '2px 10px' }}>
      <span className="muted">спрямо {koga}:</span>
      <Strelka sega={sega.total} predi={p.total} format={(x) => money(x)} />
      <Strelka sega={sega.count} predi={p.count} format={(x) => `${x} док.`} />
    </div>
  );
}
